import React, { PointerEventHandler, PointerEvent, useRef, useState } from 'react';
import { BannerComponent } from 'common/components';
import type { BannerFeatureEntityProps } from '../types';
import { AdBlock } from '../styles';
import { useIntersectionObserve } from 'common/hooks/use-intersection-observe';
import { prepareTargetUrl } from 'features/banners-carousel/utils';
import { analyticsUtils } from '../../../utils/analytics-utils';
import { extendedClick } from '../../../utils/extended-click';
import { getAnalyticsElementBlockValue } from '../utils/get-analytics-element-block-value';
import { AdData } from 'common/components/ad-data';

export const BannerFactory = ({
  bannerContent,
  showWithoutErid,
  isFromSRP,
  onImpressionContentClick,
  onImpressionContentView,
  ...rest
}: BannerFeatureEntityProps & Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick' | 'onMouseDown'>) => {
  const [isSentViewToGA, setIsSentViewToGA] = useState(false);
  const bannerRef = useRef<HTMLElement | null>(null);

  const targetUrl = prepareTargetUrl(bannerContent.banners[0].targetUrl, bannerContent.ord);

  const sendViewToGA = () => {
    analyticsUtils.sendEvent('GA:promoView', {
      elementBlock: getAnalyticsElementBlockValue(isFromSRP),
      elementType: 'banner',
      promotions: [
        {
          name: 'cat-horiz',
          creative: 'monetization',
          position: '1',
        },
      ],
    });
    setIsSentViewToGA(true);
  };

  const sendClickToGA: PointerEventHandler = (e: PointerEvent) => {
    extendedClick(e, () => {
      analyticsUtils.sendEvent('GA:promoClick', {
        elementBlock: getAnalyticsElementBlockValue(isFromSRP),
        elementType: 'banner',
        promotions: [
          {
            name: 'cat-horiz',
            creative: 'monetization',
            position: '1',
          },
        ],
      });
    });
  };

  const handleClick = () => {
    if (onImpressionContentClick) {
      onImpressionContentClick(bannerContent.id);
    }
  };

  const sendViewEvent = () => {
    if (!isSentViewToGA) {
      if (onImpressionContentView) {
        onImpressionContentView(bannerContent.id);
      }
      sendViewToGA();
    }
  };

  useIntersectionObserve(bannerRef, sendViewEvent, {
    threshold: 1,
  });

  if (!bannerContent) return null;

  if (!showWithoutErid && !bannerContent.ord?.eridToken) return null;

  return (
    <BannerComponent
      imgSrc={bannerContent?.banners[0]?.pictureUrl}
      target={targetUrl}
      isTokenExist={!!bannerContent.ord?.eridToken}
      ref={bannerRef}
      onPointerUp={e => {
        handleClick();
        sendClickToGA(e);
      }}
      {...rest}>
      <AdData className={AdBlock} ord={bannerContent.ord} />
    </BannerComponent>
  );
};
